import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';
import KpiCard from '../components/Admin/KpiCard';
import FullScreenPanel from '../components/UI/FullScreenPanel';
import ActionButton from '../components/UI/ActionButton';
import ResponsiveTable from '../components/ResponsiveTable';
import { exportToCsv } from '../utils/adminExport';
import { AuthContext } from '../context/AuthContext';

const AssignPermission = () => {
  const navigate = useNavigate();
  const { roleId: routeRoleId } = useParams();
  const { user } = useContext(AuthContext);
  const canManage = Array.isArray(user?.permissions) && user.permissions.includes('ManageConfig');
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState(routeRoleId || '');
  const [modules, setModules] = useState([]); // [string]
  const [permMap, setPermMap] = useState({}); // { module: boolean }
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newModuleName, setNewModuleName] = useState('');

  // Metrics
  const metrics = useMemo(() => {
    const totalModules = modules.length;
    const assignedCount = Object.values(permMap).filter(Boolean).length;
    return {
      totalRoles: roles.length,
      totalModules,
      assignedCount,
      unassignedCount: Math.max(0, totalModules - assignedCount),
    };
  }, [roles.length, modules, permMap]);

  // Load roles and modules
  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      try {
        const [rRes, mRes] = await Promise.all([
          axios.get(apiUrl('/api/roles'), { headers }),
          axios.get(apiUrl('/api/permissions/modules'), { headers }),
        ]);
        setRoles(rRes.data);
        setModules(mRes.data);
      } catch (e) {
        setMessage('Failed to load roles/modules');
      }
    })();
  }, []);

  // Load permissions for selected role
  useEffect(() => {
    if (!roleId) { setPermMap({}); return; }
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    axios.get(apiUrl(`/api/permissions/role/${roleId}`), { headers })
      .then(res => setPermMap(res.data || {}))
      .catch(() => setMessage('Failed to fetch role permissions'))
      .finally(() => setLoading(false));
  }, [roleId]);

  // Handlers
  const toggleModule = (mod) => {
    setPermMap(prev => ({ ...prev, [mod]: !prev[mod] }));
  };
  const setAll = (value) => {
    const next = {};
    modules.forEach(m => { next[m] = !!value; });
    setPermMap(next);
  };
  const handleSave = async () => {
    if (!roleId) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/permissions/role/${roleId}`), { permissions: permMap }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Permissions updated');
    } catch (e) {
      setMessage(e.response?.data?.error || 'Failed to update permissions');
    }
  };
  const handleExport = () => {
    if (!roleId) return;
    const rows = modules.map(m => ({ Module: m, Assigned: permMap[m] ? 'Yes' : 'No' }));
    exportToCsv(`role_${roleId}_permissions.csv`, rows);
  };
  const handleCreateModule = async () => {
    const mod = newModuleName.trim();
    if (!mod) { setMessage('Module name is required'); return; }
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(apiUrl('/api/permissions/create'), { module_name: mod }, { headers });
      // Refresh modules
      const mRes = await axios.get(apiUrl('/api/permissions/modules'), { headers });
      setModules(mRes.data);
      // extend map with new module default false
      setPermMap(prev => ({ ...prev, [mod]: prev?.[mod] ?? false }));
      setShowAddModal(false);
      setNewModuleName('');
      setMessage('Permission module created');
    } catch (e) {
      setMessage(e.response?.data?.error || 'Failed to create permission module');
    }
  };

  // Sync URL when selecting a role
  useEffect(() => {
    if (roleId) navigate(`/roles/${roleId}/permissions`, { replace: true });
  }, [roleId]);

  return (
    <div className="w-full min-h-screen">

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-3 mb-2">
        <KpiCard label="Total Roles" value={metrics.totalRoles} color="gray" />
        <KpiCard label="Modules" value={metrics.totalModules} color="blue" />
        <KpiCard label="Assigned (Role)" value={metrics.assignedCount} color="green" />
        <KpiCard label="Unassigned (Role)" value={metrics.unassignedCount} color={metrics.unassignedCount>0?'amber':'green'} />
      </div>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <label className="text-sm font-medium text-gray-800 dark:text-slate-200">Select Role:</label>
        <select
          value={roleId}
          onChange={e => setRoleId(e.target.value)}
          className="min-w-[220px]"
        >
          <option value="">-- Select Role --</option>
          {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        {roleId && canManage && (
          <>
            <button onClick={handleExport} className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600">Export CSV</button>
            <button onClick={() => setShowAddModal(true)} className="bg-green-600 text-white px-3 py-2 rounded font-medium">Add Permission</button>
            <button onClick={() => setAll(true)} className="px-3 py-2 border rounded bg-white dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600">Select All</button>
            <button onClick={() => setAll(false)} className="px-3 py-2 border rounded bg-white dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600">Select None</button>
            <ActionButton onClick={handleSave} className="bg-blue-600 text-white px-4 py-2 rounded font-semibold" busyLabel="Saving…">Save Changes</ActionButton>
          </>
        )}
      </div>

      {message && <div className="mb-3 p-2 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700">{message}</div>}

      {roleId && (
        <div className="bg-white dark:bg-slate-800">
          <ResponsiveTable
            data={modules.map((mod) => ({ mod }))}
            keyField="mod"
            columns={[
              { field: 'mod', header: 'Module' },
              {
                field: 'mod',
                header: 'Access',
                width: '7rem',
                formatter: (mod) => (
                  <input
                    type="checkbox"
                    aria-label={`Access to ${mod}`}
                    className="w-5 h-5 accent-blue-600"
                    disabled={loading || !canManage}
                    checked={!!permMap[mod]}
                    onChange={() => toggleModule(mod)}
                  />
                ),
              },
            ]}
            emptyMessage="No modules defined."
          />
        </div>
      )}

      {showAddModal && canManage && (
        <FullScreenPanel
          isOpen
          onClose={() => { setShowAddModal(false); setNewModuleName(''); }}
          title="Add Permission Module"
        >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Module Name</label>
                <input
                  value={newModuleName}
                  onChange={e => setNewModuleName(e.target.value)}
                  placeholder="Module name (e.g. ManagePermissions)"
                  className={`w-full ${newModuleName.trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
                />
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-indigo-50 dark:border-slate-700">
                <button
                  onClick={() => { setShowAddModal(false); setNewModuleName(''); }}
                  className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors font-medium text-sm border border-gray-200 dark:border-slate-600"
                  type="button"
                >
                  Cancel
                </button>
                <ActionButton
                  onClick={handleCreateModule}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm shadow-sm"
                  type="button"
                  busyLabel="Creating…"
                >
                  Create
                </ActionButton>
              </div>
            </div>
        </FullScreenPanel>
      )}
    </div>
  );
};

export default AssignPermission;